/**
 * Airport changes inside a multi-city itinerary.
 *
 * THE CASE THIS EXISTS FOR
 * ------------------------
 * The flight autocomplete offers CITIES as well as airports — a city row carries
 * a metropolitan IATA code (`LON`, `NYC`, `MIL`) and the box stores that code as
 * the leg's endpoint, exactly like an airport code. When a leg is searched with
 * one, the vendor is free to resolve it to any airport in that city, and it can
 * pick a DIFFERENT one for the arrival of leg N than for the departure of leg
 * N+1: land at Gatwick, fly out of Heathrow. Nothing on the offer card said so —
 * both legs just read "LON" in the searched route strip, and `modifyFlightData`
 * collapses a leg to `from = first.from` / `to = last.to`, which hides an airport
 * change inside a leg completely.
 *
 * WHY IT IS A PLAIN CODE COMPARISON AND NOT A CITY LOOKUP
 * ------------------------------------------------------
 * Because a city lookup does not work, and is not needed.
 *
 * It does not work: both flight mappers emit ONLY the IATA code per segment
 * (`from: { name: iataCode, code: iataCode }` — amadeus-flight.service.ts
 * flightMapper / cockpit-search.service.ts flightMapper), so grouping airports by
 * city would have to come from our own catalogue, and `tbl_locations` cannot do
 * it: airports encode their city as a name suffix (`"London Heathrow
 * Airport|London|GB-ENG"`) with no parent-city key, and that suffix disagrees
 * with the metro code for the very cases that matter — probed on staging, JFK and
 * LGA say `New York` while **EWR says `Newark`**, HND says `Tokyo` while **NRT
 * says `Narita`**, and DCA/IAD/BWI say `Washington`/`Dulles`/`Baltimore`. A
 * city-name grouping would silently miss NYC, TYO and WAS.
 *
 * It is not needed: if the traveller lands at one airport and the next flight
 * leaves from another, that is worth saying whichever cities the two are in. Two
 * airports in one city is the case this was built for; a deliberate open-jaw
 * (land TBS, leave from KUT) gets the same neutral line, which is still true and
 * still useful on a results card the user may be reading from a shared link
 * hours after typing the search.
 *
 * So: no catalogue, no metro table, no provider branching, and nothing but the
 * offer's own legs as input — which is what lets the results card, the view page
 * and the cart all call this with no extra plumbing.
 */

/**
 * The only fields this module reads from a mapped flight segment. Deliberately
 * structural and all-optional: it is handed `product_data` off a stored cart
 * line as often as a freshly mapped search offer.
 */
export interface AirportChangeSegment {
  from?: { code?: string | null } | null;
  to?: { code?: string | null } | null;
  /** Unix seconds, airport wall-clock stored as though UTC. */
  departure_date?: number | null;
  arrival_date?: number | null;
}

export interface AirportChange {
  /**
   * The leg this change is anchored to:
   *  - `withinLeg: false` — it happens immediately BEFORE leg `legIndex`
   *    departs, i.e. between leg `legIndex - 1`'s arrival and this leg's
   *    departure. Render it above leg `legIndex`.
   *  - `withinLeg: true` — it happens INSIDE leg `legIndex`, between two of its
   *    own segments. Render it on that leg.
   *
   * Always an index into the ORIGINAL `legs` array, so it stays correct for a
   * caller that filters empty legs out before rendering.
   */
  legIndex: number;
  /**
   * Index, WITHIN leg `legIndex`'s own segment array, of the segment the
   * traveller boards after the change — the one departing from `departureCode`.
   * Always `0` for a junction change (the next leg's first segment); `i + 1` for
   * a change between that leg's segments `i` and `i + 1`.
   *
   * Exists so a renderer that cannot compare siblings — the Handlebars
   * itinerary/e-ticket templates, which loop segments with no cross-section
   * visibility — can attach each notice to the right connection instead of
   * lumping every one of a leg's changes at the top of its section.
   */
  boardingIndex: number;
  /** Where the traveller lands. */
  arrivalCode: string;
  /** Where the next flight departs from. */
  departureCode: string;
  withinLeg: boolean;
  /** Under {@link TIGHT_TRANSFER_HOURS} between landing and the next departure. */
  tight: boolean;
}

/**
 * Below this many hours between landing and the next departure, a cross-airport
 * transfer stops being a note and starts being a risk to the trip.
 */
export const TIGHT_TRANSFER_HOURS = 6;

const codeOf = (location?: { code?: string | null } | null): string =>
  typeof location?.code === 'string' ? location.code.trim().toUpperCase() : '';

/**
 * Flight timestamps are the wall-clock time AT THE AIRPORT encoded as though UTC
 * (see `wallClockAtToUnixSeconds` in amadeus-flight.service.ts), so subtracting
 * two of them gives the true elapsed hours whenever both airports share a
 * timezone — which is always the case for the two-airports-in-one-city scenario
 * this flag is for. Across a timezone jump it is a wall-clock difference rather
 * than a real duration; that only ever affects an open-jaw between distant
 * cities, where the traveller has already been told the airports differ and the
 * flag is a nuance on top.
 *
 * A missing or non-finite timestamp is NOT tight: an unknown gap must not
 * manufacture an urgency warning. A negative one is — that is data claiming the
 * next flight leaves before this one lands.
 */
const isTightTransfer = (
  arrivalAt?: number | null,
  departureAt?: number | null
): boolean => {
  if (!Number.isFinite(arrivalAt) || !Number.isFinite(departureAt)) return false;
  return (Number(departureAt) - Number(arrivalAt)) / 3600 < TIGHT_TRANSFER_HOURS;
};

const appendChange = (
  out: AirportChange[],
  landing: AirportChangeSegment | undefined,
  boarding: AirportChangeSegment | undefined,
  legIndex: number,
  boardingIndex: number,
  withinLeg: boolean
): void => {
  const arrivalCode = codeOf(landing?.to);
  const departureCode = codeOf(boarding?.from);
  // An absent code is not a mismatch. A segment that never reported where it
  // landed tells us nothing, and reporting "different airports" off a blank
  // string would be a warning we invented rather than found.
  if (!arrivalCode || !departureCode || arrivalCode === departureCode) return;
  out.push({
    legIndex,
    boardingIndex,
    arrivalCode,
    departureCode,
    withinLeg,
    tight: isTightTransfer(landing?.arrival_date, boarding?.departure_date),
  });
};

/**
 * Every airport change in an N-leg itinerary, in travel order.
 *
 * Never throws and never assumes a well-formed itinerary: a null/short leg is
 * skipped rather than treated as a break in the route, so a leg junction is
 * always computed between two legs that actually carry segments. The backend
 * drops any offer with an empty leg at the source, so that is belt-and-braces
 * against a partially-written cart `product_data` — the same defence
 * ShoppingCartMultiCityLegs already applies to its own rows.
 */
export function findAirportChanges(
  legs?: (AirportChangeSegment[] | null | undefined)[] | null
): AirportChange[] {
  const flown = (Array.isArray(legs) ? legs : [])
    .map((leg, legIndex) => ({
      legIndex,
      segments: (Array.isArray(leg) ? leg : []).filter(Boolean) as AirportChangeSegment[],
    }))
    .filter((leg) => leg.segments.length > 0);

  const changes: AirportChange[] = [];

  flown.forEach(({ legIndex, segments }, position) => {
    // Between the previous flown leg and this one — the searched-city case.
    const previous = flown[position - 1];
    if (previous) {
      appendChange(
        changes,
        previous.segments[previous.segments.length - 1],
        segments[0],
        legIndex,
        0,
        false
      );
    }
    // Inside this leg — a self-transfer at a connection, invisible today
    // because the leg collapse only reports the leg's first `from` and last
    // `to`.
    for (let i = 0; i < segments.length - 1; i += 1) {
      appendChange(changes, segments[i], segments[i + 1], legIndex, i + 1, true);
    }
  });

  return changes;
}

/** Changes that happen right before leg `legIndex` departs. */
export const changesBeforeLeg = (
  changes: AirportChange[],
  legIndex: number
): AirportChange[] =>
  changes.filter((change) => !change.withinLeg && change.legIndex === legIndex);

/** Changes that happen inside leg `legIndex`. */
export const changesWithinLeg = (
  changes: AirportChange[],
  legIndex: number
): AirportChange[] =>
  changes.filter((change) => change.withinLeg && change.legIndex === legIndex);
